import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ObLoading} from '../ObLoading';
import {ClientProposalRow} from './ClientProposalRow';
import {ReviewBucket} from './ReviewBucket';
import {CapNotice} from './CapNotice';
import {GroupingToggle, multiAccountGroupCount} from './GroupingToggle';
import {ProposalToolbar} from './ProposalToolbar';
import {bucketCountsOf, clientGroupsOf, entriesOf, groupIsIncluded, includedGroupsOf, isMultiAccountGroup,
        manualWorkOf, outReasonsOf} from '../clientGroups';
import {
    adoptBorn, bucketEntriesMatching, bucketMatchesFilter, countsOf, FILTER_ALL, FILTER_BUCKET, freezeOf,
    ROWS_PER_PAGE, selectionOf, TOOLBAR_MIN_ROWS, visibleRowsOf,
} from '../proposalListView';
import {useOnboarding} from '../wizardContext';
import {REASON_PLATFORM_DISABLED} from '../wizardReducer';
import {planQuotaUsage} from '../planQuota';
import {QuotaNote} from './QuotaNote';
import {PLAN_AD_ACCOUNT_LIMIT, REQUEST_ERRORS_BEFORE_WRITING} from '../wizardPayload';
import {CLARITY_UNMASK} from '../../../utils/clarity/unmask';

/**
 * Cuánto queda a la vista el aviso del tacho, que es la única vuelta atrás de eliminar un cliente.
 *
 * Los 5 s del `<ToastContainer>` (`App.js`) son para leer una frase, no para arrepentirse: es el
 * mismo número que usa el archivo de la Memoria para su «Deshacer» (`UNDO_TOAST_MS`).
 */
export const UNDO_TOAST_MS = 8000;

/**
 * Cuánto tarda en volver a responder el tacho después de eliminar un cliente.
 *
 * Al desaparecer la fila, la de abajo sube y su tacho queda exactamente bajo el puntero: Leo lo vio
 * dos veces, con el tacho siguiente encendido como si fuera el próximo en irse (card 03NF0The). Un
 * doble clic de más se llevaba un cliente que nadie eligió. Es lo que dura un doble clic, y un poco.
 */
const REMOVE_GUARD_MS = 700;

/**
 * Cuánto se espera el gasto de 7 días antes de mostrar lo que haya (C-02, contrato §1.2). Medido el
 * 14/10 contra Meta: un BM de 400 cuentas tarda unos 30 s. Al vencer, lo no consultado queda como hoy.
 */
export const MAX_SPEND_WAIT_MS = 90000;
const DEFAULT_SPEND_RETRY_S = 5;

// A dónde va el foco si el cliente eliminado era la única fila: el tilde maestro, que sobrevive.
const FOCUS_SELECT_ALL = '__select_all__';

/** Un valor listo para ir entre comillas en un selector de atributo. */
function attributeValue(value) {
    return String(value).replace(/["\\]/g, '\\$&');
}

/**
 * Paso 3 — confirmar los clientes.
 *
 * Es el paso donde el producto admite lo que sabe de sí mismo: agrupar cuentas por su nombre acierta
 * poco, así que la pantalla no promete una cartera lista sino una propuesta revisable. Tres reglas
 * la ordenan:
 *
 * - **La cartera la elige el usuario: nada viene tildado.** Lo que el sistema propone queda a un
 *   clic —fila por fila o con el tilde maestro— pero nunca entra solo. Hasta el 2026-09-22 el
 *   criterio era el opuesto («el camino de mínimo esfuerzo llega al final sin tocar nada»), y sobre
 *   una cartera de cientos eso mandaba a crear líneas que nadie había mirado.
 * - Cuando duda, pregunta en la fila del cliente y nunca adivina en silencio.
 * - Lo que no pudo resolver queda en un cajón aparte que NO frena el recorrido: revisar esas cuentas
 *   es un trabajo esperado, no un error que haya que arreglar antes de seguir.
 */
export function StepClients() {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const {state, dispatch, server, loadProposals, refreshProposals, prepareRecentSpend} = useOnboarding();
    const [loaded, setLoaded] = useState(false);
    const [failed, setFailed] = useState(false);
    // Mientras el backend le pregunta a la plataforma qué cuentas invirtieron esta semana (C-02).
    const [checkingSpend, setCheckingSpend] = useState(false);
    // Cómo está mirando el usuario su propuesta. Es estado de VISTA y por eso vive acá y no en el
    // borrador de `wizardStorage`: no es una decisión suya sobre su cartera, y volver mañana con un
    // filtro puesto que uno no recuerda haber dejado es una pantalla que miente sobre su tamaño.
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState(FILTER_ALL);
    const [visibleCount, setVisibleCount] = useState(ROWS_PER_PAGE);
    // Cómo estaba lo que tocó la última acción masiva, para poder devolverlo tal cual.
    const [lastBulk, setLastBulk] = useState(null);
    // Un contador y no un booleano: pedir dos veces «llevame al cajón» tiene que llevar dos veces.
    // Con un booleano el segundo clic no cambia el estado y el efecto del cajón no vuelve a correr.
    const [bucketFocus, setBucketFocus] = useState(0);
    // Los editores de cliente abiertos (el último es el más reciente) y la foto del orden que se
    // tomó al abrir el primero: mientras haya uno abierto, las filas no se mueven (ver `freezeOf`).
    const [openEditors, setOpenEditors] = useState([]);
    const [freeze, setFreeze] = useState(null);
    // La fila que hay que llevar a la vista después de cerrar su editor o de deshacer su eliminación,
    // y dónde estaba, por si la lente la dejó afuera.
    const [reveal, setReveal] = useState(null);
    const [focusAfterRemove, setFocusAfterRemove] = useState(null);
    // Un contador: cada eliminación reinicia la espera del tacho (ver `REMOVE_GUARD_MS`).
    const [removeGuard, setRemoveGuard] = useState(0);
    const listRef = useRef(null);
    const visibleRef = useRef(null);
    const toastSeq = useRef(0);
    const toastIds = useRef(new Set());
    // El POST del gasto de 7 días, UNA vez por entrada al paso (contrato §1.2): el doble montaje de
    // desarrollo y cualquier remonte reusan la misma promesa en vez de volver a pedirlo.
    const spendPreparation = useRef(null);

    // Con un editor de cliente abierto, el refresco espera: mover filas mientras alguien renombra o
    // reparte cuentas le desmontaría el editor en la mano (review C-rp-fe-alta-03).
    const editorsOpen = useRef(false);
    editorsOpen.current = openEditors.length > 0;

    /**
     * La propuesta, y el gasto de 7 días que la ordena (C-02).
     *
     * Al entrar se le pide al backend que consulte a la plataforma el gasto de la semana de las cuentas
     * sin cliente: una agencia nueva no tiene foto de gasto (una cuenta sin línea no entra al sync), y
     * sin esto ninguna cuenta se sabría muerta. La pantalla NO espera ese POST: carga con el GET, y
     * cuando el POST contesta, su `preparing` arranca el sondeo si el GET no lo había arrancado ya
     * (review C-rp-fe-alta-04: un POST colgado dejaba la pantalla en el loader). Después se vuelve a
     * pedir la propuesta mientras `spend_refresh.pending`, hasta `MAX_SPEND_WAIT_MS`; un refresco que
     * no llegó (error de red) se reintenta dentro de ese tope, no se toma como «ya terminó» (review
     * C-rp-fe-alta-01). Con un backend viejo no hay POST (404) ni `spend_refresh`: el paso es el de
     * siempre.
     */
    useEffect(() => {
        let alive = true;
        let timer = null;
        let polling = false;
        const startedAt = Date.now();

        const tick = () => {
            if (!alive) return;
            const withinCap = Date.now() - startedAt < MAX_SPEND_WAIT_MS;
            if (editorsOpen.current && withinCap) {
                timer = setTimeout(tick, DEFAULT_SPEND_RETRY_S * 1000);
                return;
            }
            refreshProposals().then(proposals => {
                if (!alive) return;
                const refresh = proposals?.spend_refresh;
                const stillWaiting = proposals === null || Boolean(refresh?.pending);
                if (stillWaiting && Date.now() - startedAt < MAX_SPEND_WAIT_MS) {
                    timer = setTimeout(tick, (Number(refresh?.retry_after_seconds) || DEFAULT_SPEND_RETRY_S) * 1000);
                } else {
                    setCheckingSpend(false);
                }
            });
        };

        const startPolling = seconds => {
            if (polling || !refreshProposals) return;
            polling = true;
            setCheckingSpend(true);
            timer = setTimeout(tick, seconds * 1000);
        };

        if (!spendPreparation.current) {
            spendPreparation.current = prepareRecentSpend
                ? prepareRecentSpend().catch(() => null) : Promise.resolve(null);
        }
        loadProposals().then(result => {
            if (!alive) return;
            // Una respuesta que no llegó NO es "no tenés cuentas": pintar el vacío ante un error de
            // red le dice al usuario que su conexión no trajo nada, con botones que no van a
            // arreglar lo que pasó.
            setFailed(!result?.candidates);
            setLoaded(true);
            if (!result?.candidates) return;
            const refresh = result.proposals?.spend_refresh;
            if (refresh?.pending) startPolling(Number(refresh.retry_after_seconds) || DEFAULT_SPEND_RETRY_S);
            // Si el GET llegó antes de que la consulta arrancara, el `preparing` del POST igual pide
            // un refresco. `preparing: 0` (nada que consultar, o no se pudo encolar) no espera nada.
            spendPreparation.current.then(preparation => {
                if (!alive) return;
                if (preparation?.status === 'OK' && Number(preparation.data?.preparing) > 0) {
                    startPolling(DEFAULT_SPEND_RETRY_S);
                }
            });
        });
        return () => {
            alive = false;
            clearTimeout(timer);
        };
        // Una vez al entrar: las funciones del contexto son estables.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadProposals]);

    // Depende de `state.decisions`, que es lo único que mira `clientGroupsOf` — no del `state`
    // entero: con éste, editar un presupuesto o fallar un alta reagrupaba las 966 cuentas de nuevo.
    //
    // ⚠️ **Si `clientGroupsOf` pasa a leer otra rama de `state`, hay que sumarla acá.** Hoy lee
    // `decisions.byAccount` y `decisions.clientRenames`, las dos colgadas de `decisions`. El
    // `eslint-disable` de abajo es necesario porque a la función se le pasa el `state` entero, pero
    // apaga justo la red que avisaría del olvido: la lista quedaría corta en silencio y la pantalla
    // mostraría grupos viejos hasta que algo más la hiciera re-renderizar.
    const groups = useMemo(() => clientGroupsOf(state, server.candidates),
                           // eslint-disable-next-line react-hooks/exhaustive-deps
                           [state.decisions, server.candidates]);
    const manual = manualWorkOf(groups);
    // Lo que la pantalla promete tiene que ser lo que el paso siguiente crea: un grupo entero
    // destildado sigue siendo una fila, pero no es un cliente. Es la MISMA cuenta que hace el paso
    // de presupuestos, no una propia.
    //
    // ⚠️ Sale de `groups.assigned` ENTERO y jamás de las filas visibles: buscar o filtrar cambia lo
    // que se ve, nunca lo que se va a crear.
    const willCreate = includedGroupsOf(groups.assigned).length;
    // El cupo de cuentas del plan contra lo elegido. Bloquea ANTES de postear: el 24/09 una agencia
    // en prueba eligió 102 cuentas sobre 70 y el alta entera se cayó sin decir por qué.
    const quota = useMemo(() => planQuotaUsage({decisions: state.decisions, candidates: server.candidates,
                                                quota: server.proposals?.plan_quota}),
                          [state.decisions, server.candidates, server.proposals]);
    const overQuota = Boolean(quota && quota.over > 0);

    // ¿La propuesta quedó vacía porque la plataforma reporta cerradas a TODAS? Es distinto de que el
    // usuario las haya sacado, y el vacío tiene que decir cuál de las dos pasó.
    const allRemovedByPlatform = useMemo(
        () => groups.removed.length > 0
            && groups.removed.every(entry => entry.decision.reason === REASON_PLATFORM_DISABLED),
        [groups.removed]);

    // El cajón entra en la barra de filtros como una lente más: es la única forma de que filtrar
    // signifique lo mismo en las dos zonas de la pantalla.
    const bucket = bucketCountsOf(groups);
    const bucketCount = bucket.review + bucket.closed;
    // Cuántas cuentas del cajón responden a la búsqueda. Es lo que permite que el vacío de arriba
    // diga dónde está lo que el usuario busca en vez de afirmar que no existe.
    const bucketMatchCount = useMemo(
        () => (query
            ? bucketEntriesMatching(groups.unassigned.concat(groups.removed), query).length
            : 0),
        [groups.unassigned, groups.removed, query]);
    // Con una búsqueda puesta, cada botón cuenta lo que va a mostrar CON esa búsqueda.
    const counts = useMemo(() => countsOf(groups.assigned, query ? bucketMatchCount : bucketCount, query),
                           [groups.assigned, bucketCount, bucketMatchCount, query]);
    const editing = openEditors.length > 0;
    // El ancla es la fila que el usuario tocó por última vez con un editor: la que abrió, o en la que
    // apretó «Sacar» o «Separar» (la fila lo avisa con `onEditorToggle(key, true)`).
    const anchor = editing ? openEditors[openEditors.length - 1] : null;
    // La foto sólo manda mientras hay un editor abierto: al cerrarse el último, la lista vuelve a su
    // orden en el mismo render en que se va a buscar la fila (ver el efecto de `reveal`). Lleva el
    // ancla de AHORA, para que lo que nace en este render se dibuje debajo de la fila correcta.
    const activeFreeze = useMemo(() => {
        if (!editing || !freeze) return null;
        return freeze.anchor === anchor ? freeze : {...freeze, anchor};
    }, [editing, freeze, anchor]);
    const visible = useMemo(() => visibleRowsOf({groups: groups.assigned, query, filter, visibleCount,
                                                 frozen: activeFreeze}),
                            [groups.assigned, query, filter, visibleCount, activeFreeze]);
    const selection = useMemo(() => selectionOf(visible.matched), [visible.matched]);
    // Las filas que de verdad están dibujadas. Con la foto puesta NO es `matching`: buscar un cliente,
    // abrirlo y sacarle la cuenta buscada deja `matching` en 0 con la fila y su editor en pantalla, y
    // el vacío de «ningún cliente coincide» aparecía debajo (review C-paso3-02).
    const shownCount = visible.rows.length;
    visibleRef.current = visible;

    /**
     * La foto se toma DESPUÉS del render en que se abrió el editor, con el orden que el usuario
     * estaba viendo: todavía no cambió nada. Cambiar la lente con un editor abierto descarta la foto
     * (`resetView`) y este mismo efecto saca otra, con la lente nueva.
     *
     * Y lo que nace mientras tanto se ADOPTA acá, apenas aparece: queda fijo debajo de la fila que lo
     * produjo aunque después se abra otro editor (ver `adoptBorn`).
     */
    useEffect(() => {
        if (!editing) {
            if (freeze) setFreeze(null);
            return;
        }
        if (!freeze) {
            setFreeze(freezeOf(visibleRef.current, groups.assigned, anchor));
            return;
        }
        const born = groups.assigned.map(group => group.key).filter(key => !freeze.known.has(key));
        if (born.length) setFreeze(adoptBorn({...freeze, anchor}, born));
    }, [editing, freeze, anchor, groups.assigned]);

    const onEditorToggle = useCallback((key, isOpen) => {
        setOpenEditors(keys => {
            if (isOpen && keys[keys.length - 1] === key) return keys;
            const rest = keys.filter(each => each !== key);
            return isOpen ? rest.concat(key) : rest;
        });
    }, []);

    const onEditorClosed = useCallback(key => {
        const rows = visibleRef.current?.rows || [];
        setReveal({key, fromIndex: Math.max(0, rows.findIndex(group => group.key === key))});
    }, []);

    /**
     * Al cerrar el editor, la lista se reordena y la fila se trae a la vista con el foco.
     *
     * Reordenar es inevitable —la fila cambió de tamaño o pasó a «listos»— y lo que no puede pasar
     * es que el usuario la pierda: se trae a la vista, con el foco en el «Editar» que la abrió.
     * Si su lugar nuevo quedó más allá de la tanda pintada, se destapa hasta ella; si la lente ya no
     * la muestra (la eligió con «Sin confirmar» puesto), el foco va a la fila que ocupa su lugar,
     * que es la siguiente a revisar.
     *
     * `block: 'nearest'` y no `'center'`: cerrar un editor sin cambios no puede mover la pantalla. Si
     * la fila ya está a la vista no se scrollea, y si no, entra por el borde más cercano, respetando
     * las barras pegajosas (el `scroll-margin` de `.ob-prop`).
     */
    useEffect(() => {
        if (!reveal || editing) return;
        const position = visible.sequence.findIndex(group => group.key === reveal.key);
        if (position >= visible.rows.length) {
            setVisibleCount(Math.ceil((position + 1) / ROWS_PER_PAGE) * ROWS_PER_PAGE);
            return;
        }
        setReveal(null);
        const list = listRef.current;
        if (!list) return;
        const own = position >= 0
            ? list.querySelector(`[data-group-key="${attributeValue(reveal.key)}"]`) : null;
        const rows = list.querySelectorAll('.ob-prop');
        const target = own || rows[Math.min(reveal.fromIndex, rows.length - 1)];
        if (!target) return;
        if (target.scrollIntoView) target.scrollIntoView({block: 'nearest'});
        const control = (own && target.querySelector('.ob-prop__act[aria-expanded]'))
            || target.querySelector('.ob-check');
        if (control) control.focus({preventScroll: true});
    }, [reveal, editing, visible]);

    /**
     * El tacho: elimina, avisa con un «Deshacer» y no deja el foco donde estaba.
     *
     * Eliminar un cliente propuesto no pedía confirmación ni tenía vuelta atrás a la vista: lo
     * eliminado bajaba al cajón, al final de cientos de filas (card 03NF0The). El aviso es la red, y
     * cada eliminación tiene el suyo: con un id compartido, eliminar dos seguidos dejaba al primero
     * sin deshacer.
     */
    const onRemoved = useCallback((group, {pks, hadFocus}) => {
        const rows = visibleRef.current?.rows || [];
        const index = rows.findIndex(each => each.key === group.key);
        const neighbour = index >= 0 ? (rows[index + 1] || rows[index - 1] || null) : null;
        if (hadFocus) setFocusAfterRemove(neighbour ? neighbour.key : FOCUS_SELECT_ALL);
        setRemoveGuard(times => times + 1);

        toastSeq.current += 1;
        const toastId = `ob-removed-${toastSeq.current}`;
        const undo = event => {
            // `stopPropagation`: el `<ToastContainer>` de la app cierra con `closeOnClick`, y el
            // mismo clic no puede cerrar el aviso ANTES de deshacer.
            event.stopPropagation();
            dispatch({type: 'SET_DISMISSED_MANY', pks, dismissed: false});
            toast.dismiss(toastId);
            toastIds.current.delete(toastId);
            setReveal({key: group.key, fromIndex: Math.max(0, index)});
        };
        // Sin `role` propio: el contenedor de react-toastify ya anuncia el aviso (`role="alert"`), y
        // uno anidado lo hacía leer dos veces.
        toast(
            <div className="ob-toast">
                <span className="ob-toast__msg">
                    {t('onboarding.clients.delete_undo', {name: group.name, interpolation: {escapeValue: false}})}
                </span>
                <button type="button" className="ob-toast__undo" onClick={undo}>
                    {t('onboarding.clients.undo')}
                </button>
            </div>,
            {toastId, autoClose: UNDO_TOAST_MS});
        toastIds.current.add(toastId);
    }, [dispatch, t]);

    // El foco del tacho va a la casilla de la fila que quedó en su lugar: nunca al tacho de al lado,
    // que es el próximo cliente que se iría con un Enter de más.
    //
    // Si era la ÚNICA fila, no queda casilla ni tilde maestro (la barra de selección desaparece sin
    // filas), y el foco caía en el `<body>` (review C-paso3-07): va a la primera salida del vacío, si
    // no al buscador, y si tampoco hay (cartera chica), al cajón, que es donde quedó lo eliminado.
    useEffect(() => {
        if (!focusAfterRemove) return;
        setFocusAfterRemove(null);
        const target = focusAfterRemove === FOCUS_SELECT_ALL
            ? (document.querySelector('.ob-select .ob-check')
               || document.querySelector('.ob-no-matches button')
               || document.querySelector('.ob-search__input')
               || document.querySelector('.ob-bucket__head'))
            : listRef.current?.querySelector(`[data-group-key="${attributeValue(focusAfterRemove)}"] .ob-check`);
        if (target) target.focus({preventScroll: true});
    }, [focusAfterRemove]);

    useEffect(() => {
        if (!removeGuard) return undefined;
        const timer = setTimeout(() => setRemoveGuard(0), REMOVE_GUARD_MS);
        return () => clearTimeout(timer);
    }, [removeGuard]);

    // Los avisos son de este paso: su «Deshacer» despacha contra el estado del túnel que se monta
    // con cada paso, así que en el siguiente no haría nada.
    useEffect(() => {
        const ids = toastIds.current;
        return () => ids.forEach(id => toast.dismiss(id));
    }, []);

    /**
     * Cuando la lente vacía la lista, el mensaje que lo explica tiene que quedar A LA VISTA.
     *
     * Escribiendo en la barra —que es sticky— con la lista ya scrolleada, la lista se achica y el
     * scroll se queda donde estaba: el vacío aterriza detrás de la barra y sólo se ve el borde de
     * una caja punteada. Medido en el QA: `.ob-no-matches` quedaba con `top: 53`, debajo del
     * stepper. El estado vacío es justo el que hay que poder leer.
     *
     * ⚠️ El contenedor de scroll del túnel es `.ob-tunnel` (`position: fixed; overflow-y: auto`),
     * no la ventana: un `window.scrollTo` no mueve nada. Y va instantáneo, no `smooth`: compite con
     * el tecleo del buscador, que filtra en vivo.
     */
    useEffect(() => {
        if (shownCount > 0) return;
        const tunnel = document.querySelector('.ob-tunnel');
        if (tunnel && tunnel.scrollTop > 0) tunnel.scrollTo({top: 0});
    }, [shownCount]);

    // Cambiar de lente vuelve a la primera tanda: sin esto, filtrar con 600 filas ya desplegadas
    // pintaría las 600 de nuevo, que es justo lo que el paginado evita.
    //
    // Y borra el deshacer: es de la lista que el usuario estaba mirando, y una vez que cambió de
    // recorte ya no se acuerda de qué revertiría — deshacer algo que uno no tiene presente es peor
    // que no poder deshacerlo.
    //
    // Y descarta la foto del orden: cambiar la lente con un editor abierto es pedir OTRA lista, y
    // congelar la vieja sería no hacerle caso. Si el editor sigue abierto, se saca una foto nueva.
    const resetView = () => {
        setVisibleCount(ROWS_PER_PAGE);
        setLastBulk(null);
        setFreeze(null);
    };
    const changeQuery = value => {
        setQuery(value);
        resetView();
    };
    const changeFilter = value => {
        setFilter(value);
        resetView();
    };
    const clearLens = () => {
        setQuery('');
        setFilter(FILTER_ALL);
        resetView();
    };
    // «Mostrar más» NO descarta la foto: con un editor abierto destapa las filas siguientes de la foto,
    // que guarda el orden entero. Tirarla mandaba la fila editada a su lugar nuevo, que podía estar
    // 150 filas más abajo (review C-paso3-05).
    const showMore = () => setVisibleCount(count => count + ROWS_PER_PAGE);
    /**
     * Ir al cajón: el chip de arriba y el «ver las que coinciden» del vacío.
     *
     * Si la lente puesta no alcanza al cajón («Listos para crear», «Con dudas»), se cambia a la del
     * cajón: llevar al usuario a una caja que el filtro dibuja vacía es no llevarlo a ningún lado.
     */
    const goToBucket = () => {
        if (!bucketMatchesFilter(filter)) changeFilter(FILTER_BUCKET);
        setBucketFocus(times => times + 1);
    };

    /**
     * Tildar (o destildar) todo lo que la lente está mostrando.
     *
     * Antes de tocar nada se anota cómo estaba cada cuenta, y el deshacer restituye eso mismo — no
     * «todas apagadas», que es lo que dejaría una acción inversa ingenua sobre una lista que ya
     * tenía cosas tildadas a mano.
     */
    const selectAll = include => {
        // Se anota el tilde Y el «sin confirmar»: el masivo apaga los dos, y un deshacer que
        // devuelve sólo el tilde deja la fila indistinguible de una que el usuario sacó a mano.
        const before = {on: [], off: [], unconfirmed: []};
        selection.pks.forEach(pk => {
            const decision = state.decisions.byAccount[pk];
            (decision?.include ? before.on : before.off).push(pk);
            if (decision?.unconfirmed) before.unconfirmed.push(pk);
        });
        setLastBulk(before);
        dispatch({type: 'SET_INCLUDE_MANY', pks: selection.pks, include});
    };

    /**
     * Sacar de la tanda los clientes que fallaron y crear todos los demas.
     *
     * Es la salida del modo de falla que hizo abandonar el alta: el backend ahora reporta TODOS
     * los items rotos de una vez, pero sin esto el usuario igual tiene que destildarlos a mano uno
     * por uno —«si me pongo a destildar 290 clientes no terminamos mas»—. Se destildan, no se
     * eliminan: quedan en la lista, marcados, para resolverlos despues desde la cartera.
     *
     * Destildar cambia la firma del pedido, que es lo que habilita a `applyPlan` a volver a
     * postear; sin ese cambio el reintento se considera el mismo pedido y no sale.
     */
    const createTheRest = () => {
        const failed = groups.assigned.filter(group =>
            group.accounts.concat(group.doubts).some(entry => state.apply.rowErrors[entry.pk]));
        const pks = failed.flatMap(group => group.accounts.concat(group.doubts).map(entry => entry.pk));
        if (!pks.length) return;
        dispatch({type: 'SET_INCLUDE_MANY', pks, include: false});
        // El pedido explícito: sin esto la pantalla de espera no postea (ver `applyPlanFor`).
        dispatch({type: 'APPLY_REQUESTED'});
        navigate('/onboarding/creando');
    };

    const undoBulk = () => {
        if (lastBulk.on.length) dispatch({type: 'SET_INCLUDE_MANY', pks: lastBulk.on, include: true});
        if (lastBulk.off.length) dispatch({type: 'SET_INCLUDE_MANY', pks: lastBulk.off, include: false});
        // Va al final y por separado: las «sin confirmar» son un subconjunto de las destildadas,
        // así que este dispatch las vuelve a marcar después de que el anterior las apagó.
        if (lastBulk.unconfirmed?.length) {
            dispatch({type: 'SET_INCLUDE_MANY', pks: lastBulk.unconfirmed, include: false,
                      unconfirmed: true});
        }
        setLastBulk(null);
    };
    // La agrupación es la CUARTA lente y por un tiempo fue la única que no volvía a la primera
    // tanda: medido en producción con Demo, desplegar hasta 800 filas y volver a «agrupadas»
    // montaba las 526 de una (9.777 nodos) en vez de 100. No colgaba, pero es exactamente lo que
    // las tres de arriba evitan — y el modo nuevo tiene otra cantidad de filas, así que arrastrar
    // el `visibleCount` del modo anterior no significa nada para el usuario.
    const changeGrouping = grouping => {
        dispatch({type: 'SET_GROUPING', grouping});
        resetView();
    };

    // La consulta más cara del recorrido: con mil cuentas deja la pantalla entera en blanco varios
    // segundos. Se dice qué se está haciendo, y que el tamaño de la cartera explica la demora — sin
    // eso, la espera más larga del túnel es también la única que no da ninguna señal.
    if (!loaded) {
        return <ObLoading titleKey="onboarding.loading.clients"
                          hintKey="onboarding.loading.clients_hint"/>;
    }

    if (failed) return <CouldNotLoad onRetry={() => window.location.reload()}/>;

    const allAccounts = server.candidates?.accounts || [];
    const accountCount = allAccounts.filter(account => !account.covered).length;
    // Las que YA tienen línea no entran a la propuesta, y esa resta es lo que hace que el paso 2
    // diga «Encontramos 1145 cuentas» y éste hable de 1119: el paso anterior cuenta el padrón
    // entero. Sin nombrarlas, el número baja entre una pantalla y la siguiente sin ningún motivo a
    // la vista — que es exactamente la clase de detalle que hace desconfiar del resto.
    const coveredCount = allAccounts.length - accountCount;
    // Las cuentas que de verdad quedaron DENTRO de los clientes propuestos. El subtítulo usaba el
    // padrón sin cubrir, que incluye las del cajón y las descartadas: en producción eso afirmaba
    // «Agrupamos 1119 cuentas … en 177 clientes» mientras el cajón de la misma pantalla decía que
    // 894 habían quedado afuera. Sobrestimaba 5,8×, y su propia pantalla lo desmentía más abajo.
    const groupedAccountCount = groups.assigned.reduce(
        (total, group) => total + entriesOf(group).length, 0);
    // Cuántos de los grupos VIVOS juntan varias cuentas — la misma definición que usa el renglón
    // del tilde maestro, para que los dos números de la pantalla no se contradigan.
    const liveMultiAccount = groups.assigned.filter(isMultiAccountGroup).length;
    const hasNothing = accountCount === 0;
    // Buscar y filtrar aparecen cuando la lista es un muro. Sobre una cartera chica son ruido que
    // hace parecer complicado algo que se resuelve mirando.
    //
    // El cajón cuenta (review C-rp-fe-alta-02 de la ronda 2): con C-02 la mayoría de las cuentas de
    // una agencia vieja baja a «Sin inversión esta semana», y con 10 propuestas y 390 en el cajón no
    // había dónde buscar. Y con una búsqueda o un filtro puesto la barra no se va: un refresco que baja
    // las propuestas de 30 le sacaba el buscador de la mano a quien lo estaba usando.
    const showToolbar = groups.assigned.length + bucketCount >= TOOLBAR_MIN_ROWS
        || Boolean(query) || filter !== FILTER_ALL;
    const hasBothSections = visible.showSections;

    // Vacío porque TODAS las cuentas ya tienen línea es otra cosa que vacío porque la conexión no
    // trajo nada: a la agencia que vuelve al túnel con su cartera armada, «La conexión no trajo
    // cuentas con actividad» le dice algo falso —las trajo, todas cubiertas— y le ofrece conectar
    // otra plataforma para arreglar un problema que no tiene. Es el mismo «un cero que no es un
    // cero» que el paso 2 ya distingue.
    if (hasNothing) return <NoAccountsFound allCovered={coveredCount}/>;

    return (
        <div className="ob-step-body -wide">
            {/* El subtítulo dice qué encontramos; la pastilla dice qué clase de cosa es esto.
                Estaban en el mismo párrafo de tres renglones, y el «todo se puede cambiar después»
                —lo único que baja la ansiedad de aprobar una lista de trescientos clientes que uno
                no armó— quedaba al final, que es donde nadie llega. Es el mismo recurso que usa el
                paso de presupuestos para separar la explicación de la consecuencia. */}
            <header className="ob-head">
                <h1 {...CLARITY_UNMASK}>{t('onboarding.clients.title')}</h1>
                {/* El subtítulo dice el TOTAL y cómo se reparte, y las partes suman el total a la
                    vista: agrupadas + para revisar + cerradas. Antes sólo decía las agrupadas
                    («Agrupamos 120 cuentas en 95 clientes») y el resto aparecía en otros lugares
                    con otros números: el paso 2 había dicho 386, el cajón 990, el pie «7 de las
                    70». Leo, en su alta: «ahí tengo un quilombo grande» (card 2yINyKQv). El total
                    es la SUMA de las tres y no se recalcula aparte, para que cuadre siempre.

                    Los números están vivos: sacar una cuenta de un cliente la pasa de «agrupadas» a
                    «revisión», y el total no se mueve. Lo que el usuario ELIGIÓ lo dice el botón
                    del pie, no esta frase.

                    Cada número con plural propio es su propia clave: i18next pluraliza por UN solo
                    `count`, y con los números a pelo una agencia con una cuenta leía «1 cuentas». */}
                {/* La última oración dice para qué sirve agrupar: un cliente es la unidad que siguen
                    el tablero y la IA. Va en el MISMO párrafo: el encabezado ya apilaba cuatro
                    bloques de texto antes del primer control, y a 390 no entraba ningún cliente en
                    la primera vista (QA-VIS-journey-06). */}
                <p>{t('onboarding.clients.subtitle', {
                    count: groupedAccountCount + bucket.review + bucket.closed,
                    grouped: groupedAccountCount,
                    clients: t('onboarding.clients.client_count', {count: groups.assigned.length}),
                    interpolation: {escapeValue: false},
                })}{outSummaryOf(outReasonsOf(groups), t, i18n?.language)} {t('onboarding.clients.why')}</p>
                {coveredCount > 0 && (
                    <p className="ob-note">
                        {t('onboarding.clients.already_covered', {count: coveredCount})}
                    </p>
                )}
                {/* Mientras llega el gasto de 7 días, la lista se puede mover sola: las cuentas que
                    no invirtieron bajan al cajón. Se dice, para que eso no parezca un error. Lo que
                    el usuario ya tocó no se mueve (ver `reconcile`). */}
                {checkingSpend && (
                    <p className="ob-note ob-spend-check" role="status">
                        <span className="ob-spinner-mini" aria-hidden="true"/>
                        {t('onboarding.clients.spend_checking')}
                    </p>
                )}
                <p className="ob-head__what" {...CLARITY_UNMASK}>{t('onboarding.clients.is_a_proposal')}</p>
            </header>

            <ApplyErrorBanner apply={state.apply} groups={groups.assigned}
                              onCreateTheRest={createTheRest}/>

            <CapNotice stats={server.proposals?.stats}/>

            <GroupingToggle
                grouping={state.grouping}
                groupCount={multiAccountGroupCount(server.proposals)}
                liveGroupCount={liveMultiAccount}
                onChange={changeGrouping}
            />

            {/* Con la barra puesta, los chips de «agrupados» y «dudas» repetirían lo que ya dicen
                sus filtros, y encima sin poder tocarse.
                El chip «220 cuentas para revisar» se fue (H-04, recorrido con Pablo del 13/10):
                pedía revisar cuentas que conviene ignorar. Lo que queda afuera lo dice el resumen de
                arriba, y al cajón se llega por el filtro «Quedan afuera (N)» o por el propio cajón,
                al final de la lista. */}
            {!showToolbar && (
                <div className="ob-chips">
                    <span className="ob-chip -auto">
                        {t('onboarding.clients.chip_auto', {count: willCreate})}
                    </span>
                    {manual.doubts > 0 && (
                        <span className="ob-chip -doubt">{t('onboarding.clients.chip_doubts', {count: manual.doubts})}</span>
                    )}
                </div>
            )}

            {/* La barra va SIEMPRE, aunque la lente no: el tilde maestro es lo que hace evidente
                que las filas se pueden tildar, y eso hace falta con doce filas igual que con
                seiscientas. Buscar y filtrar, en cambio, sobre una lista corta son ruido. */}
            {/* Mientras llega el gasto de 7 días, «Seleccionar todo» espera: tildar todo antes
                elegiría también las cuentas que están por resultar sin inversión, y lo tildado no se
                mueve solo (review C-rp-fe-alta-01: así se creaban como clientes). Tildar de a una
                sigue disponible. */}
            <ProposalToolbar counts={counts} filter={filter} onFilter={changeFilter}
                             query={query} onQuery={changeQuery} showLens={showToolbar}
                             selection={selection} onSelectAll={selectAll}
                             waitForSpend={checkingSpend}
                             onUndo={lastBulk ? undoBulk : null}/>

            {/* `-guard`: el tacho no responde un instante después de eliminar (`REMOVE_GUARD_MS`). */}
            <div className={`ob-proposals${removeGuard ? ' -guard' : ''}`} ref={listRef}>
                {/* Qué es cada columna. Leo lo pidió mirando la lista: «una columna que explique:
                    este es el nombre del cliente y estas son las cuentas asociadas». En angosto la
                    fila se apila y no hay columnas que rotular, así que se va (ver el scss). */}
                {visible.rows.length > 0 && (
                    <div className="ob-proposals__cols" aria-hidden="true">
                        <span className="ob-proposals__col -client">{t('onboarding.clients.col_client')}</span>
                        <span className="ob-proposals__col">{t('onboarding.clients.col_accounts')}</span>
                    </div>
                )}
                {/* Los encabezados sólo aparecen si hay DOS secciones. Con la cartera entera
                    resuelta —o entera pendiente— un título que anuncia un corte que no existe es
                    ruido, y encima haría parecer que falta una mitad. Dónde va el segundo lo dice
                    `splitAt`: con un editor abierto las filas no se mueven, así que el corte es el
                    de la foto y no el de ahora. */}
                {visible.rows.map((group, position) => (
                    <React.Fragment key={group.key}>
                        {hasBothSections && position === 0 && (
                            <h2 className="ob-proposals__section">
                                {t('onboarding.clients.section_ready', {count: visible.readyCount})}
                            </h2>
                        )}
                        {hasBothSections && position === visible.splitAt && (
                            <h2 className="ob-proposals__section -pending">
                                {t('onboarding.clients.section_pending', {count: visible.pendingCount})}
                            </h2>
                        )}
                        <ClientProposalRow group={group} dispatch={dispatch}
                                           rowErrors={state.apply.rowErrors}
                                           onEditorToggle={onEditorToggle} onEditorClosed={onEditorClosed}
                                           onRemoved={onRemoved}/>
                    </React.Fragment>
                ))}
            </div>

            {/* Una lista vacía sin explicación se lee como que la propuesta se perdió. El total
                dice que sigue entera y el botón devuelve a ella sin tener que adivinar qué lente
                está puesta.

                Vacía y sin lente puesta es otra cosa: el usuario sacó todo. Ofrecerle «limpiar la
                búsqueda» ahí lo mandaría a buscar un filtro que no existe, y «ver los 0 clientes
                propuestos» es una frase sin sentido. */}
            {visible.total === 0 ? (
                <div className="ob-no-matches" role="status">
                    {/* Y vacía porque la PLATAFORMA reporta cerradas a todas es una tercera cosa. El
                        copy de «sacaste todo» le atribuiría al usuario un gesto que no hizo, que es
                        justo lo que rompe la confianza en una pantalla que recién conoce. */}
                    <p>{allRemovedByPlatform
                        ? t('onboarding.clients.all_platform_disabled')
                        : t('onboarding.clients.all_removed')}</p>
                </div>
            ) : shownCount === 0 && filter !== FILTER_BUCKET && (
                <div className="ob-no-matches" role="status">
                    {/* Vacío porque lo que se busca está ABAJO es otra cosa que vacío a secas, y es
                        el caso que rompió la confianza en el buscador: Pablo buscó «Dashbo», leyó
                        «ningún cliente coincide» y tenía la cuenta de Meta a 200 px, adentro del
                        cajón plegado — la terminó encontrando de casualidad. El buscador SÍ mira el
                        nombre de la cuenta y su id; lo que faltaba era decir dónde está lo que
                        encontró, y llevar hasta ahí. */}
                    <p>{bucketMatchCount > 0
                        ? t('onboarding.clients.no_matches_but_bucket', {count: bucketMatchCount})
                        : t('onboarding.clients.no_matches')}</p>
                    {bucketMatchCount > 0 && (
                        <button type="button" className="ob-link" onClick={goToBucket}>
                            {t('onboarding.clients.go_to_bucket_matches', {count: bucketMatchCount})}
                        </button>
                    )}
                    <button type="button" className="ob-link" onClick={clearLens}>
                        {t('onboarding.clients.clear_lens', {count: visible.total})}
                    </button>
                </div>
            )}

            {/* Decir SIEMPRE qué quedó afuera es el contrato del módulo — el mismo que sostiene el
                aviso del tope del agrupador. Una lista recortada en silencio se lee como completa, y
                el usuario cierra el paso creyendo que vio su cartera entera. */}
            {/* `matching > 0` porque con la lista vacía el vacío YA explica lo que pasó: debajo
                seguía un «Mostrando 0 de los 0 que coinciden, sobre 178 en total» que repite peor
                lo mismo, y encima con el «0 de los 0» que es texto de máquina. */}
            {filter !== FILTER_BUCKET && shownCount > 0
                && (visible.hidden > 0 || visible.matching < visible.total) && (
                <div className="ob-more">
                    <p className="ob-more__count">
                        {visible.matching < visible.total
                            ? t('onboarding.clients.showing_filtered',
                                {shown: visible.rows.length, matching: visible.matching, total: visible.total})
                            : t('onboarding.clients.showing',
                                {shown: visible.rows.length, total: visible.total})}
                    </p>
                    {visible.hidden > 0 && (
                        <button type="button" className="ob-btn -ghost" onClick={showMore}>
                            {t('onboarding.clients.show_more',
                               {count: Math.min(visible.hidden, ROWS_PER_PAGE)})}
                        </button>
                    )}
                </div>
            )}

            {/* `filter` va junto con `query`: hasta acá sólo viajaba la búsqueda, así que la lente
                gobernaba media pantalla y el bloque de revisión seguía entero abajo de un «ningún
                cliente coincide». Son la misma lente y tienen que llegar juntas. */}
            <ReviewBucket focusSignal={bucketFocus}
                          pending={groups.unassigned} removed={groups.removed} groups={groups.assigned}
                          dispatch={dispatch} query={query} filter={filter}
                          onReveal={() => changeFilter(FILTER_BUCKET)}/>

            {/* Fijo: con una cartera larga el «Continuar» quedaba al fondo, después del cajón. */}
            <footer className="ob-foot -sticky">
                {/* La salida manual, al lado de la automática y no escondida en el estado vacío: que
                    el sistema proponga no puede leerse como que es el único camino. Va al listado,
                    que es donde vive el alta de un cliente; el borrador de este armado queda
                    guardado, así que volver no cuesta nada. */}
                <button type="button" className="ob-link" onClick={() => navigate('/agency/clients')}>
                    {t('onboarding.clients.prefer_manual')}
                </button>
                <QuotaNote quota={quota}/>
                {/* El botón se apaga con la MISMA cuenta que muestra: con el conteo viejo podía
                    quedar habilitado ofreciendo «Continuar con 0 clientes». Y con la cartera por
                    encima del cupo del plan: la nota de al lado dice por qué y cuántas sobran. */}
                <button type="button" className="ob-btn -primary" disabled={willCreate === 0 || overQuota}
                        onClick={() => navigate('/onboarding/presupuestos')}>
                    {t('onboarding.clients.continue', {count: willCreate})}
                </button>
            </footer>
        </div>
    );
}

/**
 * La segunda oración del subtítulo: cuántas cuentas quedan afuera y por qué (H-04).
 *
 * Decía «220 necesitan revisión y 57 están cerradas», que le pedía al usuario revisar cuentas que
 * conviene ignorar. Ahora es una sola frase, «277 quedan afuera: sin campañas activas o cerradas en
 * la plataforma», y nombra SÓLO los motivos que hay (ver `outReasonsOf`): sin catálogo, el cajón de
 * una agencia nueva son cuentas que no se pudieron agrupar, y decir «sin campañas activas» de ellas
 * sería falso. Sin nada afuera, no hay oración.
 *
 * Los motivos concuerdan con el total («1 queda afuera: cerrada…»), que es el sujeto de la frase.
 */
export function outSummaryOf(out, t, language) {
    if (!out.total) return '';
    const reasons = [];
    if (out.noSpend) reasons.push(t('onboarding.clients.out_reason.no_spend'));
    if (out.dormant) reasons.push(t('onboarding.clients.out_reason.dormant'));
    if (out.closed) reasons.push(t('onboarding.clients.out_reason.closed', {count: out.total}));
    if (out.unclear) reasons.push(t('onboarding.clients.out_reason.unclear'));
    if (out.taken) reasons.push(t('onboarding.clients.out_reason.taken', {count: out.total}));
    const sentence = t('onboarding.clients.out_summary', {count: out.total,
                                                          reasons: alternativesOf(reasons, language),
                                                          interpolation: {escapeValue: false}});
    return ` ${sentence}`;
}

/** «a, b o c» / «a, b, or c», con la regla de cada idioma. Sin `Intl.ListFormat`, con comas. */
function alternativesOf(items, language) {
    if (typeof Intl !== 'undefined' && Intl.ListFormat) {
        return new Intl.ListFormat(language || 'es', {type: 'disjunction'}).format(items);
    }
    return items.join(', ');
}

/**
 * El título del cartel dice CUÁNTO se creó, porque es lo primero que el usuario necesita saber.
 *
 * Tres casos: se creó una parte (`error_partial`), no se pudo crear toda (errores por fila), o no se
 * escribió nada porque el backend rechazó el pedido entero antes de tocar la base (el cupo). Con el
 * título genérico en ese último caso, «No pudimos crear toda tu cartera» daba a entender que había
 * una parte creada (review C-TUNELDEUDA-03).
 */
function titleOf(apply, t) {
    if (apply.status === 'error_partial') return t('onboarding.apply_error.partial');
    const requestErrors = apply.requestErrors || [];
    const nothingWritten = !Object.keys(apply.rowErrors || {}).length && requestErrors.length > 0
        && requestErrors.every(error => REQUEST_ERRORS_BEFORE_WRITING.includes(error.code));
    return nothingWritten ? t('onboarding.apply_error.title_nothing_created') : t('onboarding.apply_error.title');
}

/** El rechazo por cupo del plan, con sus números: cuántas cuentas sobran es la acción concreta. */
function QuotaErrorReason({details}) {
    const {t} = useTranslation();
    const {limit = 0, used = 0, requested_new: requested = 0} = details;
    // Lo que sobra es lo pedido menos lo DISPONIBLE, y lo disponible no baja de cero: con una
    // suscripción que bajó de plan (`used > limit`) la cuenta `used + requested - limit` pedía quitar
    // más cuentas de las que tiene la cartera. Es el mismo criterio que la nota del pie.
    const available = typeof details.available === 'number' ? details.available : Math.max(0, limit - used);

    return (
        <p className="ob-apply-error__reason">
            {t('onboarding.apply_error.reason.plan_ad_account_limit',
               {count: Math.max(0, requested - available), limit, used, requested})}
        </p>
    );
}

/**
 * Qué pasó cuando el alta falló.
 *
 * Sin esto el usuario apretaba «Crear 12 líneas», veía el spinner y aparecía de nuevo acá sin
 * ningún mensaje y sin nada marcado: no tenía forma de saber qué salió mal ni qué arreglar.
 *
 * Los errores se nombran por CLIENTE y no por cuenta, que es como está mirando la pantalla, y el
 * mensaje sale de un diccionario propio por código: el texto del backend no se muestra nunca.
 */
function ApplyErrorBanner({apply, groups, onCreateTheRest}) {
    const {t} = useTranslation();
    if (apply.status !== 'error_items' && apply.status !== 'error_partial') return null;

    const affected = groups.filter(group =>
        group.accounts.concat(group.doubts).some(entry => apply.rowErrors[entry.pk]));
    const codes = Array.from(new Set(Object.values(apply.rowErrors).flat().map(error => error.code)));
    // Los errores del PEDIDO (sin fila). El del cupo se dice con sus números y va aparte de los
    // demás; el resto usa el diccionario de siempre.
    const requestErrors = apply.requestErrors || [];
    const quotaError = requestErrors.find(error => error.code === PLAN_AD_ACCOUNT_LIMIT);
    const requestCodes = Array.from(new Set(requestErrors.map(error => error.code)))
        .filter(code => code !== PLAN_AD_ACCOUNT_LIMIT && !codes.includes(code));
    // Lo que queda en pie si se sacan los fallados. Sale de `groupIsIncluded`, la misma definicion
    // que usa el boton «Crear N lineas»: dos numeros distintos para la misma cartera, en la misma
    // pantalla, seria peor que no ofrecer la salida.
    const rest = groups.filter(group => groupIsIncluded(group) && !affected.includes(group)).length;

    return (
        <div className="ob-apply-error" role="alert">
            <p className="ob-apply-error__title">
                {titleOf(apply, t)}
            </p>
            {quotaError && <QuotaErrorReason details={quotaError.details || {}}/>}
            {codes.concat(requestCodes).map(code => (
                <p className="ob-apply-error__reason" key={code}>
                    {t(`onboarding.apply_error.reason.${String(code).toLowerCase()}`,
                       t('onboarding.apply_error.reason.generic'))}
                </p>
            ))}
            {affected.length > 0 && (
                <p className="ob-apply-error__clients">
                    {t('onboarding.apply_error.affected', {clients: affected.map(group => group.name).join(', '),
                                                           interpolation: {escapeValue: false}})}
                </p>
            )}
            {apply.createdClients.length > 0 && (
                <p className="ob-apply-error__created">
                    {t('onboarding.apply_error.already_created',
                       {count: apply.createdClients.length,
                        clients: apply.createdClients.map(client => client.name).join(', '),
                        interpolation: {escapeValue: false}})}
                </p>
            )}

            {/* La salida, y es la mitad del arreglo: reportar los 40 items rotos de una vez no
                sirve de nada si despues hay que destildarlos a mano uno por uno. Solo aparece si
                queda algo que crear: ofrecer «crear el resto» cuando el resto es cero seria una
                promesa vacia. */}
            {/* Con el cupo excedido «crear el resto» no tiene resto: el pedido entero no entra y el
                que decide qué queda afuera es el usuario, destildando (el pie dice cuántas). */}
            {onCreateTheRest && rest > 0 && !quotaError && (
                <div className="ob-apply-error__actions">
                    <button type="button" className="ob-btn -primary -sm" onClick={onCreateTheRest}>
                        {t('onboarding.apply_error.create_the_rest', {count: rest})}
                    </button>
                    <span className="ob-apply-error__hint">
                        {t('onboarding.apply_error.create_the_rest_hint')}
                    </span>
                </div>
            )}
        </div>
    );
}

/**
 * La conexión no trajo ninguna cuenta con actividad.
 *
 * No es un error del usuario ni una pantalla de fracaso: es una credencial sin clientes asociados o
 * cuentas sin campañas. Se dice eso y se ofrecen las dos salidas reales, sin el botón de continuar
 * —no hay nada con qué continuar— para que la única acción visible sea una que sirva.
 */
/** No se pudo leer la propuesta. Distinto de "no hay cuentas", y con una salida que sirve. */
function CouldNotLoad({onRetry}) {
    const {t} = useTranslation();

    return (
        <div className="ob-step-body">
            <header className="ob-head">
                <h1 {...CLARITY_UNMASK}>{t('onboarding.clients.title')}</h1>
            </header>
            <div className="ob-placeholder">
                <h2 {...CLARITY_UNMASK}>{t('onboarding.clients.failed_title')}</h2>
                <p {...CLARITY_UNMASK}>{t('onboarding.clients.failed_hint')}</p>
                <div className="ob-placeholder__actions">
                    <button type="button" className="ob-btn -primary" onClick={onRetry} {...CLARITY_UNMASK}>
                        {t('onboarding.clients.failed_retry')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function NoAccountsFound({allCovered = 0}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const covered = allCovered > 0;

    return (
        <div className="ob-step-body">
            <header className="ob-head">
                <h1 {...CLARITY_UNMASK}>{t('onboarding.clients.title')}</h1>
                <p {...CLARITY_UNMASK}>{covered
                    ? t('onboarding.clients.all_covered_subtitle')
                    : t('onboarding.clients.empty_subtitle')}</p>
            </header>
            <div className="ob-placeholder">
                <h2>{covered
                    ? t('onboarding.clients.all_covered_title', {count: allCovered})
                    : t('onboarding.clients.empty_title')}</h2>
                <p {...CLARITY_UNMASK}>{covered
                    ? t('onboarding.clients.all_covered_hint')
                    : t('onboarding.clients.empty_hint')}</p>
                <div className="ob-placeholder__actions">
                    <button type="button" className="ob-btn -primary" {...CLARITY_UNMASK}
                            onClick={() => navigate('/onboarding/plataformas')}>
                        {t('onboarding.clients.empty_connect')}
                    </button>
                    {/* Al listado, que es donde vive el alta de un cliente: no hay una pantalla
                        propia para crear uno. */}
                    <button type="button" className="ob-btn -ghost" onClick={() => navigate('/agency/clients')} {...CLARITY_UNMASK}>
                        {t('onboarding.clients.empty_manual')}
                    </button>
                </div>
            </div>
        </div>
    );
}
